import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AddCircleBlue, CustomDelete, EditWithIcon, MinLoader, NoItemsYet } from 'components';
import { OptionDetailsNotYet } from './constants';
import { httpRequestsOnErrorsActions, httpRequestsOnSuccessActions, libraryActions } from 'store';
import {
    Colors,
    convertSecondToMinute,
    FindErrorItem,
    FindLoadItem,
    FindSuccessItem,
    goalDataTypeEnums,
    Images,
    isNotEmpty,
    useModal,
} from 'utils';
import { renderOptionField } from '../../../fragments/client/clientSingle/clientClinical/goals/targets/constants';

export const OptionsDetails = ({ currentFolder, requestFoldersParams, isExpanded }) => {
    const [showCreatingInputs, toggleCreatingInputs] = useState(false);
    const [inputs, setInputs] = useState({});
    const [error, setError] = useState('');
    const dispatch = useDispatch();
    const ACTION_TYPE = inputs?.id ? 'EDIT_LIBRARY_OPTION' : 'CREATE_LIBRARY_OPTION';
    const loading = FindLoadItem(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const { open } = useModal();

    useEffect(() => {
        if (success?.type) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(success));
            toggleCreatingInputs(false);
            setInputs({});
        }
    }, [success]);

    useEffect(() => {
        if (!isExpanded) {
            setInputs({});
            toggleCreatingInputs(false);
        }
    }, [isExpanded]);

    const handleAdd = () => {
        toggleCreatingInputs(true);
    };

    const handleChange = (e) => {
        setInputs((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
        if (error === e.target.name) {
            setError('');
        }
    };

    const resetCreation = () => {
        setError('');
        setInputs({ value: '0' });
        toggleCreatingInputs(false);
        dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
    };

    const handleAction = (type, item) => {
        if (type === 'delete') {
            open(
                <CustomDelete
                    text="Are you sure you want to delete this option? This action cannot be undone."
                    info="Delete Option"
                    handleDel={() => dispatch(libraryActions.deleteLibraryOption(currentFolder?.id, item?.id, requestFoldersParams))}
                    actionType={'DELETE_LIBRARY_OPTION'}
                    innerText={'Delete'}
                />
            );
        } else {
            setError('');
            toggleCreatingInputs(true);
            setInputs(item);
        }
    };

    const handleSave = () => {
        if (isNotEmpty(inputs?.label) && (inputs?.value || inputs?.value === '0' || inputs?.value === 0)) {
            const body = {
                label: inputs?.label,
                value: +inputs?.value,
            };

            if (inputs?.timeType) {
                body.timeType = inputs?.timeType;
                body.value = convertSecondToMinute(inputs?.timeType, inputs?.value);
            }

            if (inputs?.id) {
                dispatch(libraryActions.editLibraryOption(currentFolder?.id, inputs?.id, body, requestFoldersParams));
            } else {
                dispatch(libraryActions.createLibraryOption(currentFolder?.id, body, requestFoldersParams));
            }
        } else {
            setError(!isNotEmpty(inputs?.label) ? 'label' : !inputs?.value ? 'value' : '');
        }
    };

    return (
        <div className="targets-tab-details-wrapper">
            <div className="target-options-wrapper">
                {!showCreatingInputs && !!currentFolder?.options?.length && (
                    <div className="flex-end" style={{ padding: '0 12px 8px' }}>
                        <AddCircleBlue type="button" text={'Add Option'} handleClick={() => handleAction('edit', {})} />
                    </div>
                )}

                {currentFolder?.options?.length ? (
                    <div className="targets-tab-details-block">
                        {currentFolder?.options?.map((option, index) => (
                            <div
                                key={index}
                                className="target-options-row"
                                style={{
                                    gap: 10,
                                    marginBottom: 0,
                                }}
                            >
                                <p className="option-label-box">{option?.label || '—'}</p>

                                <div className="flex-align-center" style={{ gap: 16 }}>
                                    {renderOptionField(currentFolder, option)}

                                    <div className="flex-align-center" style={{ gap: 12 }}>
                                        <EditWithIcon
                                            id={'edit-form'}
                                            icon={Images.editAuth}
                                            type={'button'}
                                            handleClick={(e) => {
                                                e.stopPropagation();
                                                handleAction('edit', option);
                                            }}
                                        />

                                        <EditWithIcon
                                            id={'delete-form'}
                                            icon={Images.deleteTag}
                                            type={'button'}
                                            handleClick={(e) => {
                                                e.stopPropagation();
                                                handleAction('delete', option);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    !showCreatingInputs && (
                        <NoItemsYet
                            image={OptionDetailsNotYet.image}
                            btnText={OptionDetailsNotYet.btnText}
                            text={OptionDetailsNotYet.title}
                            subTitle={OptionDetailsNotYet.subTitle}
                            handleAdd={handleAdd}
                            containerStyles={{
                                position: 'static',
                            }}
                            height="fit-content"
                        />
                    )
                )}

                {showCreatingInputs && (
                    <div className="target-options-row" style={{ gap: 10 }}>
                        {currentFolder?.dataType === goalDataTypeEnums?.LATENCY ||
                        currentFolder?.dataType === goalDataTypeEnums?.WHOLE_INTERVAL ||
                        currentFolder?.dataType === goalDataTypeEnums?.PARTIAL_INTERVAL ||
                        currentFolder?.dataType === goalDataTypeEnums?.MOMENTARY_TIME_SAMPLING ? (
                            <p className="option-label-box">{inputs?.label}</p>
                        ) : (
                            <input
                                value={inputs?.label || ''}
                                onChange={handleChange}
                                placeholder="Write Option Name"
                                className="target-option-input"
                                type="text"
                                name="label"
                                style={{
                                    borderColor: backError?.error ? '#FE7070' : error === 'label' ? '#FE7070' : undefined,
                                }}
                            />
                        )}
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            {renderOptionField(currentFolder, inputs, handleChange, error)}

                            <div className="flex-align-center" style={{ gap: 16 }}>
                                <button onClick={handleSave} className="save-option-btn">
                                    {loading?.loadingType ? (
                                        <MinLoader margin="0" color={Colors.ThemeBlue} position="relative" width={13} height={13} />
                                    ) : (
                                        <img src={Images.checkmarkBlue} alt="icon" />
                                    )}
                                    <p>{inputs?.id ? 'Save' : 'Add'}</p>
                                </button>
                                <button onClick={resetCreation} className="cancel-option-btn">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
